/* eslint-disable react/prop-types */
import { Check } from "@phosphor-icons/react";

/**
 * Phase 7 — Visual breadcrumb showing where the doc sits in the lifecycle.
 * Reads existing fields only: doc_type, status, approval_status, linked_*.
 * Read-only, additive, no API calls.
 */
const STEPS = [
  { key: "quote",    label: "Quote" },
  { key: "proforma", label: "Proforma" },
  { key: "invoice",  label: "Invoice" },
  { key: "so",       label: "Sales Order" },
  { key: "paid",     label: "Paid" },
];

function deriveProgress(doc) {
  // Returns { reachedIdx, currentIdx, badges: { stepKey: numberLabel } }
  if (!doc) return { reachedIdx: -1, currentIdx: -1, badges: {} };
  const badges = {};
  let reachedIdx = -1;

  // Quote step
  const isQuote = doc.doc_type === "estimation";
  const isPF    = doc.doc_type === "proforma";
  const isInv   = doc.doc_type === "invoice";

  if (isQuote || isPF || isInv) { reachedIdx = 0; badges.quote = doc.doc_type === "estimation" ? doc.number : (doc.source_estimation_number || ""); }

  if (isPF || isInv || doc.linked_proforma_number) {
    reachedIdx = Math.max(reachedIdx, 1);
    badges.proforma = isPF ? doc.number : (doc.linked_proforma_number || doc.source_proforma_number || "");
  }

  if (isInv || doc.linked_invoice_number) {
    reachedIdx = Math.max(reachedIdx, 2);
    badges.invoice = isInv ? doc.number : (doc.linked_invoice_number || "");
  }

  if (doc.linked_so_number) {
    reachedIdx = Math.max(reachedIdx, 3);
    badges.so = doc.linked_so_number;
  }

  if (isInv && doc.status === "paid") {
    reachedIdx = 4;
    badges.paid = "Paid";
  } else if (isInv && doc.status === "partial") {
    badges.paid = `Partial · ₹${Number(doc.amount_paid || 0).toLocaleString("en-IN")}`;
  }

  // Current step = the last reached
  const currentIdx = reachedIdx;
  return { reachedIdx, currentIdx, badges };
}

export default function BillingFlowProgress({ doc, onStepClick }) {
  if (!doc) return null;
  const { reachedIdx, badges } = deriveProgress(doc);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 print:hidden" data-testid="billing-flow-progress">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-2">Document Lifecycle</div>
      <div className="flex items-center gap-1">
        {STEPS.map((s, idx) => {
          const reached = idx <= reachedIdx;
          const current = idx === reachedIdx;
          const badge = badges[s.key];
          const clickable = !!badge && onStepClick;
          return (
            <div key={s.key} className="flex items-center flex-1 min-w-0">
              <button
                disabled={!clickable}
                onClick={() => clickable && onStepClick(s.key, badge)}
                data-testid={`flow-step-${s.key}`}
                className={`flex flex-col items-start gap-0.5 px-3 py-1.5 rounded-md border transition-colors min-w-0 flex-1 text-left ${
                  current
                    ? "bg-indigo-600 border-indigo-600 text-white"
                    : reached
                      ? "bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100"
                      : "bg-slate-50 border-slate-200 text-slate-400"
                } ${clickable ? "cursor-pointer" : "cursor-default"}`}
              >
                <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold">
                  {reached && !current && <Check size={11} weight="bold" />}
                  {s.label}
                </div>
                {badge ? (
                  <div className={`text-xs font-mono truncate w-full ${current ? "text-white/90" : ""}`}>{badge}</div>
                ) : (
                  <div className="text-[10px] opacity-50">—</div>
                )}
              </button>
              {idx < STEPS.length - 1 && (
                <div className={`h-px flex-shrink-0 w-3 mx-0.5 ${idx < reachedIdx ? "bg-emerald-300" : "bg-slate-200"}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
